import { createContext, useCallback, useContext, useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import type { NavItem } from '../../components/elbklar/sidebar';
import { toast } from '../../components/ui/toast';
import { mitAenderung } from './logik';
import type {
  AlleGelesen,
  Ansicht,
  AppKennung,
  Benachrichtigung,
  BenachrichtigungenApi,
  BenachrichtigungsSeite,
  Einstellungen,
  EinstellungsAenderung,
  Zaehler,
} from './typen';

const SCHLUESSEL = ['benachrichtigungen'] as const;
const PRO_SEITE = 20;
/** So oft fragt jede App nach neuen Meldungen (das CRM tat es schon so). */
const ABFRAGE_MS = 30_000;

export interface Kontext {
  app: AppKennung;
  api: BenachrichtigungenApi;
  navigieren: (pfad: string) => void;
  /** Adresse der Einstellungen in Konto, mit Rückweg in diese App. */
  kontoEinstellungen: () => string;
  offen: boolean;
  setOffen: (offen: boolean) => void;
}

export const BenachrichtigungenKontext = createContext<Kontext | null>(null);

function useKontext(): Kontext {
  const k = useContext(BenachrichtigungenKontext);
  if (!k) throw new Error('Benachrichtigungen brauchen einen <BenachrichtigungenProvider> darüber.');
  return k;
}

/** Für die App: ungelesene Anzahl, öffnen und der Eintrag für den Sidebar-Fuß. */
export function useBenachrichtigungen() {
  const { setOffen } = useKontext();
  const zaehler = useZaehler();
  const ungelesen = zaehler.data?.unread_count ?? 0;
  const oeffnen = useCallback(() => setOffen(true), [setOffen]);
  const eintrag: NavItem = useMemo(
    () => ({ id: 'benachrichtigungen', label: 'Benachrichtigungen', icon: Bell, count: ungelesen > 0 ? ungelesen : undefined }),
    [ungelesen],
  );
  /** Für `Sidebar onSelect`: `true`, wenn die ID der Eintrag war (dann ist das Panel offen). */
  const waehlen = useCallback(
    (id: string) => {
      if (id !== eintrag.id) return false;
      setOffen(true);
      return true;
    },
    [eintrag.id, setOffen],
  );
  return { ungelesen, oeffnen, eintrag, waehlen };
}

// Intern für das Panel.

export function useZaehler() {
  const { api } = useKontext();
  return useQuery({
    queryKey: [...SCHLUESSEL, 'zaehler'],
    queryFn: () => api.get<Zaehler>('/notifications/count'),
    refetchInterval: ABFRAGE_MS,
    refetchOnWindowFocus: true,
  });
}

export function useListe(ansicht: Ansicht, seite: number, aktiv: boolean) {
  const { api } = useKontext();
  return useQuery({
    queryKey: [...SCHLUESSEL, 'liste', ansicht, seite],
    queryFn: () => api.get<BenachrichtigungsSeite>('/notifications', { ansicht, page: seite, per_page: PRO_SEITE }),
    enabled: aktiv,
    // Immer frisch beim Öffnen und solange offen im Takt des Zählers, auch
    // wenn die App für ihre Abfragen ein langes staleTime voreinstellt (PM: 5 min).
    staleTime: 0,
    refetchInterval: aktiv ? ABFRAGE_MS : false,
    placeholderData: keepPreviousData,
  });
}

/** Gelesen markieren: sofort im Stand, danach Liste und Zähler vom Server. */
export function useGelesen() {
  const { api } = useKontext();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Benachrichtigung>(`/notifications/${encodeURIComponent(id)}/read`),
    onMutate: (id) => {
      qc.setQueriesData<BenachrichtigungsSeite>({ queryKey: [...SCHLUESSEL, 'liste'] }, (s) =>
        s && { ...s, items: s.items.map((b) => (b.id === id ? { ...b, is_read: true } : b)) },
      );
      qc.setQueryData<Zaehler>([...SCHLUESSEL, 'zaehler'], (z) => z && { unread_count: Math.max(0, z.unread_count - 1) });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: SCHLUESSEL }),
  });
}

export function useAlleGelesen() {
  const { api } = useKontext();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AlleGelesen>('/notifications/read-all'),
    onSuccess: (r) => qc.setQueryData<Zaehler>([...SCHLUESSEL, 'zaehler'], { unread_count: r.unread_count }),
    onSettled: () => qc.invalidateQueries({ queryKey: SCHLUESSEL }),
  });
}

export function useEinstellungen(aktiv: boolean) {
  const { api } = useKontext();
  return useQuery({
    queryKey: [...SCHLUESSEL, 'einstellungen'],
    queryFn: () => api.get<Einstellungen>('/notifications/preferences'),
    enabled: aktiv,
    staleTime: 60_000,
  });
}

type Aenderung =
  | { art: 'ereignis'; event_key: string; kanal: string; aenderung: EinstellungsAenderung }
  | { art: 'kategorie'; kategorie: string; kanal: string; aenderung: EinstellungsAenderung };

/**
 * Speichert je Klick, sofort sichtbar. Änderungen laufen nacheinander
 * (`scope`), damit bei schnellem Umschalten (auch per Pfeiltaste) der letzte
 * Stand auf dem Server gewinnt; die Antwort des letzten Aufrufs wird übernommen.
 */
export function useEinstellungAendern() {
  const { api } = useKontext();
  const qc = useQueryClient();
  const schluessel = [...SCHLUESSEL, 'einstellungen'];
  return useMutation({
    scope: { id: 'benachrichtigungen-einstellungen' },
    mutationFn: (a: Aenderung) =>
      a.art === 'ereignis'
        ? api.put<Einstellungen>('/notifications/preferences', { updates: [{ event_key: a.event_key, channel: a.kanal, ...a.aenderung }] })
        : api.put<Einstellungen>('/notifications/preferences/category', { category: a.kategorie, channel: a.kanal, ...a.aenderung }),
    onMutate: (a) => {
      const vorher = qc.getQueryData<Einstellungen>(schluessel);
      if (vorher) {
        const betroffen = a.art === 'ereignis' ? (e: { event_key: string }) => e.event_key === a.event_key : (e: { category: string }) => e.category === a.kategorie;
        qc.setQueryData<Einstellungen>(schluessel, { ...vorher, events: mitAenderung(vorher.events, betroffen, a.kanal, a.aenderung) });
      }
      return { vorher };
    },
    onError: (_f, _a, ctx) => {
      if (ctx?.vorher) qc.setQueryData(schluessel, ctx.vorher);
      // Auch als Toast: Die Meldung oben in den Einstellungen sieht nicht, wer
      // weiter unten geklickt hat — dann wirkt der Klick, als täte er nichts.
      toast.error('Die Änderung ließ sich nicht speichern', { description: 'Der vorige Stand ist wiederhergestellt.' });
    },
    onSuccess: (antwort) => {
      // Nur die Antwort der letzten offenen Änderung übernehmen, sonst springt der Stand zurück.
      if (qc.isMutating({ mutationKey: undefined, predicate: (m) => m.options.scope?.id === 'benachrichtigungen-einstellungen' }) <= 1) {
        qc.setQueryData(schluessel, antwort);
      }
      // Ein abgeschaltetes Ereignis verschwindet aus Liste und Zähler.
      qc.invalidateQueries({ queryKey: [...SCHLUESSEL, 'liste'] });
      qc.invalidateQueries({ queryKey: [...SCHLUESSEL, 'zaehler'] });
    },
  });
}

/** Zum Öffnen eines Eintrags im Panel. */
export function useNavigation() {
  const { app, navigieren, kontoEinstellungen, setOffen } = useKontext();
  return { app, navigieren, kontoEinstellungen, schliessen: () => setOffen(false) };
}
